export interface SectionTiming {
  position: number;
  durationMs: number;
  startedAt: number;
  completedAt?: number;
}

export class SectionBoundaryDetector {
  private readonly sectionCount: number;
  private readonly now: () => number;

  private _currentSectionIndex = 0;
  private _timings: SectionTiming[] = [];
  private _started = false;
  private _paused = false;
  private _pausedAt = 0;
  private _totalPausedMs = 0;
  private _startTime = 0;

  constructor(sectionCount: number, now?: () => number) {
    this.sectionCount = sectionCount;
    this.now = now ?? (() => performance.now());
  }

  get currentSectionIndex(): number {
    return this._currentSectionIndex;
  }

  get sectionTimings(): SectionTiming[] {
    return [...this._timings];
  }

  get totalDurationMs(): number {
    if (!this._started) return 0;
    const elapsed = this.now() - this._startTime;
    const pausedTime = this._paused
      ? this._totalPausedMs + (this.now() - this._pausedAt)
      : this._totalPausedMs;
    return elapsed - pausedTime;
  }

  start(): void {
    const now = this.now();
    this._started = true;
    this._startTime = now;
    this._timings = [
      {
        position: 0,
        durationMs: 0,
        startedAt: now,
      },
    ];
  }

  onBoundaryCrossed(sectionIndex: number): void {
    if (sectionIndex <= this._currentSectionIndex) return;

    const now = this.now();
    const current = this._timings[this._timings.length - 1];
    if (current) {
      current.completedAt = now;
      current.durationMs = now - current.startedAt - this.pausedDuringSectionMs(current.startedAt);
    }

    this._currentSectionIndex = sectionIndex;
    this._timings.push({
      position: sectionIndex,
      durationMs: 0,
      startedAt: now,
    });
  }

  pause(): void {
    if (this._paused || !this._started) return;
    this._paused = true;
    this._pausedAt = this.now();
  }

  resume(): void {
    if (!this._paused) return;
    this._totalPausedMs += this.now() - this._pausedAt;
    this._paused = false;
  }

  reset(): void {
    this._currentSectionIndex = 0;
    this._timings = [];
    this._started = false;
    this._paused = false;
    this._pausedAt = 0;
    this._totalPausedMs = 0;
    this._startTime = 0;
  }

  private pausedDuringSectionMs(sectionStartedAt: number): number {
    // Approximate: attribute all paused time proportionally
    // For simplicity, track total paused time since start
    if (!this._started) return 0;
    const totalElapsed = this.now() - this._startTime;
    if (totalElapsed === 0) return 0;
    const sectionElapsed = this.now() - sectionStartedAt;
    const currentPaused = this._paused
      ? this._totalPausedMs + (this.now() - this._pausedAt)
      : this._totalPausedMs;
    // Scale paused time by section proportion
    return (sectionElapsed / totalElapsed) * currentPaused;
  }
}
